import { useLayoutEffect, useRef } from "react";
import type { AnimationControls } from "framer-motion";

/**
 * Whether the calling component is still mounted, tracked in a layout effect:
 * the same moment framer-motion's `useAnimation()` marks its controls mounted
 * and unmounted.
 */
export const useMountedRef = () => {
  const mounted = useRef(false);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  return mounted;
};

/**
 * The "order created" flash on the submit section.
 *
 * Order submits start it without awaiting, once an async submit resolves, and by
 * then the trade form can have unmounted (closed, or replaced by a market
 * switch). In development framer-motion's controls throw from `start()` after
 * the component that created them unmounts (an uncaught promise rejection);
 * production builds skip that check and would animate a detached element.
 * Each step therefore runs only while that component is still mounted.
 */
export const playOrderCreatedAnimation = async (
  controls: Pick<AnimationControls, "start">,
  isMounted: () => boolean
) => {
  if (!isMounted()) return;
  await controls.start({
    opacity: 1,
    scale: 1,
    transition: { duration: 0.6 },
  });
  if (!isMounted()) return;
  await controls.start({
    opacity: 0,
    scale: 0,
    transition: {
      delay: 3,
      duration: 0.5,
    },
  });
};
